import type {
  AssessmentDraftQuestion,
  SelfAssessmentConfig,
  SelfAssessmentPaper,
} from '@/pages/learn/study/studySelfAssessmentApi';
import type { StudyAnswerCheck } from '@/pages/learn/study/studyAnalysisFollowUpApi';
import { DEMO_ANALYSIS_ID, DEMO_WORKBOOK_ID } from './demoLearningOutcomes';

export const DEMO_ASSESSMENT_ID = 'demo-assessment-complete';
const config = (
  title: string,
  count: number,
  kinds: SelfAssessmentConfig['question_kinds'],
  difficulty: SelfAssessmentConfig['difficulty'],
): SelfAssessmentConfig => ({
  title,
  question_count: count,
  question_kinds: kinds,
  difficulty,
  key_point_indices: [],
});
export const DEMO_ASSESSMENT_PRESETS = [
  {
    key: 'mixed',
    label: '5 questions · mixed types · standard',
    document_id: DEMO_WORKBOOK_ID,
    config: config(
      'Alex’s mathematics self-assessment — prepared example',
      5,
      ['multiple_choice', 'short_answer', 'working'],
      'standard',
    ),
  },
  {
    key: 'easy',
    label: '3 questions · multiple choice · easy',
    document_id: DEMO_WORKBOOK_ID,
    config: config('Maths foundations — prepared example', 3, ['multiple_choice'], 'easy'),
  },
  {
    key: 'challenge',
    label: '5 questions · show working · challenge',
    document_id: DEMO_WORKBOOK_ID,
    config: config('Connect and explain — prepared example', 5, ['working'], 'challenge'),
  },
];
const q = (
  kind: AssessmentDraftQuestion['kind'],
  stem: string,
  answer: string,
  solution: string,
  options: string[] | null = null,
  correct: AssessmentDraftQuestion['correct_option'] = null,
): AssessmentDraftQuestion => ({
  kind,
  stem,
  answer,
  worked_solution: solution,
  options,
  correct_option: correct,
});
const mixed = [
  q(
    'multiple_choice',
    'What is 1/3 + 1/9?',
    '4/9',
    'Use equal-sized ninths: 1/3 = 3/9. Add 3/9 + 1/9 = 4/9. Keep denominator 9. Check the sum lies between 1/3 and 1/2.',
    ['2/12', '2/9', '4/9', '4/12'],
    'C',
  ),
  q(
    'multiple_choice',
    'Share 40 units in the ratio 2:3. Which pair preserves both the total and the ratio?',
    '16 and 24',
    'The whole contains 2+3=5 parts. Each is 40/5=8. Shares: 2×8=16 and 3×8=24. Check 16+24=40 and 16:24=2:3. Taking 2/3 of the whole confuses part-to-part with part-to-whole.',
    ['26⅔ and 13⅓', '16 and 24', '20 and 20', '24 and 16'],
    'B',
  ),
  q(
    'short_answer',
    'A cone has radius 3 cm and perpendicular height 4 cm. Give its exact volume.',
    '12π cm³',
    'V=(1/3)πr²h=(1/3)π×3²×4=12π cm³. The perpendicular height is 4 cm. Slant length 5 cm is for curved area, not volume. The matching cylinder has volume 36π cm³; the cone is one third.',
  ),
  q(
    'working',
    'Solve 3x + 2 = 14. Show inverse operations and check in the original equation.',
    'x = 4',
    'Subtract 2 from both sides: 3x=12. Divide both sides by 3: x=4. Substitute into the original equation: 3(4)+2=14. Both sides agree.',
  ),
  q(
    'short_answer',
    'A sphere has radius 2 cm. Give its exact surface area.',
    '16π cm²',
    'Surface area S=4πr²=4π×2²=16π cm². This is area, so use square centimetres. Volume uses r³ and a different formula.',
  ),
];
const easy = [
  q(
    'multiple_choice',
    'A right triangle has perpendicular sides 3 cm and 4 cm. What is the hypotenuse?',
    '5 cm',
    'c²=3²+4²=9+16=25. The positive length is √25=5 cm. The hypotenuse is longer than each leg.',
    ['1 cm', '5 cm', '7 cm', '25 cm'],
    'B',
  ),
  q(
    'multiple_choice',
    'What is 12.5% of 80 units?',
    '10 units',
    '12.5%=0.125=1/8. Calculate 80×1/8=10. Check 10/80×100%=12.5%.',
    ['1 unit', '8 units', '10 units', '12.5 units'],
    'C',
  ),
  mixed[1],
];
const challenge = [
  q(
    'working',
    'Keep a cone’s radius at 3 cm and change its perpendicular height from 4 cm to 12 cm. Find the new volume and total surface area; explain which triples.',
    'V = 36π cm³; S = 9π + 9π√17 cm²',
    'Slant l=√(3²+12²)=√153=3√17 cm. V=(1/3)π×9×12=36π cm³, three times the original 12π. S=πr²+πrl=9π+9π√17≈144.852 cm². Surface area does not triple: the base stays fixed and the slant changes nonlinearly.',
  ),
  q(
    'working',
    'A sphere’s radius increases from 2 cm to 4 cm. Calculate both new measures and compare them with the originals.',
    'S = 64π cm²; V = 256π/3 cm³',
    'At r=4: S=4π×16=64π cm² and V=(4/3)π×64=256π/3 cm³. Original measures at r=2 are 16π and 32π/3. The ratios are 4 and 8, from squaring and cubing the radius scale factor 2.',
  ),
  q(
    'working',
    'Solve 3x + 2 = 3 exactly and explain why 0.33 does not satisfy it exactly.',
    'x = 1/3',
    'Subtract 2: 3x=1. Divide by 3: x=1/3. Exact check: 3×(1/3)+2=3. Rounded check: 3×0.33+2=2.99, not 3. Keep the fraction until final rounding.',
  ),
  q(
    'working',
    'Share 60 units in the ratio 2:3 and verify both conditions.',
    '24 and 36',
    'There are 2+3=5 parts. One part is 60/5=12. Shares are 2×12=24 and 3×12=36. Verify 24+36=60 and 24:36=2:3. The first share is 2/5 of the total, not 2/3.',
  ),
  q(
    'working',
    'Solve x² − 5x + 6 = 0 using the discriminant and check both roots.',
    'x = 2 or x = 3',
    'a=1,b=−5,c=6. D=b²−4ac=25−24=1. x=(5±1)/2 gives 2 and 3. Check 2²−5×2+6=0 and 3²−5×3+6=0. Two distinct real roots agree with D>0.',
  ),
];
export function demoAssessmentQuestions(key: string): AssessmentDraftQuestion[] {
  return structuredClone(key === 'easy' ? easy : key === 'challenge' ? challenge : mixed).map(
    (row, index) => ({ ...row, id: `pq${index + 1}` }),
  );
}
export function preparedAssessmentAttempt(
  question: AssessmentDraftQuestion,
  index: number,
  paperId: string,
): StudyAnswerCheck {
  const wrong = index === 1;
  const stamp = '2026-10-17T05:00:00.000Z';
  return {
    id: `prepared-assessment-${index}`,
    question_id: `pq${index + 1}`,
    practice_set_id: paperId,
    status: 'checked',
    verdict: wrong ? 'incorrect' : 'correct',
    hint: wrong
      ? 'Prepared judgement: count all five equal parts before sharing.'
      : 'Prepared judgement: this answer matches the reference method.',
    student_answer_text:
      question.kind === 'multiple_choice'
        ? wrong
          ? 'A'
          : question.correct_option!
        : question.kind === 'working'
          ? question.worked_solution
          : question.answer,
    stars: 0,
    stars_refunded: 0,
    refunded: false,
    error_code: null,
    created_at: stamp,
    completed_at: stamp,
  };
}
export function demoAssessmentPaper(
  key = 'mixed',
  id = DEMO_ASSESSMENT_ID,
  completed = true,
): SelfAssessmentPaper {
  const preset = DEMO_ASSESSMENT_PRESETS.find((p) => p.key === key)!;
  const questions = demoAssessmentQuestions(key);
  return {
    id,
    analysis_id: DEMO_ANALYSIS_ID,
    document_id: DEMO_WORKBOOK_ID,
    status: 'ready',
    assessment: structuredClone(preset.config),
    phase: completed ? 'published' : 'draft',
    revision: 1,
    check_available: true,
    question_kinds: [...preset.config.question_kinds],
    stars_charged: 0,
    stars_refunded: 0,
    refunded: false,
    check_stars_per_attempt: 0,
    error_code: null,
    created_at: '2026-10-17T04:30:00.000Z',
    completed_at: '2026-10-17T04:30:00.000Z',
    ...(completed ? {} : { draft_questions: questions }),
    questions: questions.map((question, index) => ({
      id: question.id!,
      kind: question.kind,
      stem: question.stem,
      options: question.options,
      attempts: completed ? 1 : 0,
      answer_available: completed,
      answer: completed
        ? {
            answer: question.answer,
            correct_option: question.correct_option,
            worked_solution: question.worked_solution,
          }
        : null,
      latest_attempt: completed ? preparedAssessmentAttempt(question, index, id) : null,
    })),
  };
}
